import { motion } from "framer-motion";
import { Phone, ShoppingBag, Utensils, CalendarDays, Truck, Info } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useTranslation, PHONES } from "../i18n";

const CallButton = ({ testid }) => (
  <div data-testid={testid} className="flex flex-col sm:flex-row gap-3">
    {PHONES.map((p) => (
      <a
        key={p.tel}
        href={`tel:${p.tel}`}
        data-testid={`call-link-${p.tel}`}
        className="btn-primary-q5 group"
      >
        <Phone size={14} className="transition-transform group-hover:rotate-12" />
        {p.display}
      </a>
    ))}
  </div>
);

export const CallSection = () => {
  const { lang } = useApp();
  const t = useTranslation(lang);

  return (
    <section id="call" data-testid="call-section" className="relative py-24 md:py-32 bg-[#0E0E0E] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <img
          src="/q5/interior.png"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="overline">{t.call.overline}</span>
          <h2 className="font-serif-q5 text-4xl md:text-5xl mt-3 text-white">{t.call.title}</h2>
          <p className="text-neutral-400 mt-5 max-w-2xl mx-auto leading-relaxed">{t.call.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {/* Reserva */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="card-reserve"
            className="bg-[#141414] border border-q5 hover:border-gold transition-colors p-7 flex flex-col"
          >
            <div className="w-12 h-12 border border-gold flex items-center justify-center mb-5">
              <CalendarDays size={20} className="text-gold" />
            </div>
            <h3 className="font-serif-q5 text-2xl text-white mb-3">{t.call.reserve}</h3>
            <p className="text-sm text-neutral-400 leading-relaxed flex-grow">
              {lang === "es"
                ? "Reserva tu mesa con antelación. Atendemos la llamada al momento y te confirmamos disponibilidad."
                : "Book your table in advance. We answer the phone right away and confirm availability."}
            </p>
            <div className="mt-6 pt-5 border-t border-q5 space-y-1.5">
              {PHONES.map((p) => (
                <a
                  key={p.tel}
                  href={`tel:${p.tel}`}
                  data-testid={`reserve-call-${p.tel}`}
                  className="block text-white hover:text-gold transition-colors text-sm flex items-center gap-2"
                >
                  <Phone size={12} className="text-gold" />
                  {p.display}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Recogida */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="card-pickup"
            className="bg-[#141414] border border-gold/40 hover:border-gold transition-colors p-7 flex flex-col relative"
          >
            <span className="absolute top-3 right-3 bg-gold/15 text-gold text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-gold/40">
              -10%
            </span>
            <div className="w-12 h-12 border border-gold flex items-center justify-center mb-5">
              <ShoppingBag size={20} className="text-gold" />
            </div>
            <h3 className="font-serif-q5 text-2xl text-white mb-3">{t.call.pickup}</h3>
            <p className="text-sm text-gold leading-relaxed mb-3 font-medium">
              {t.call.pickupBenefit}
            </p>
            <p className="text-sm text-neutral-400 leading-relaxed flex-grow">
              {lang === "es"
                ? "Pide por teléfono y recoge en el local sin esperas."
                : "Order by phone and pick up at the restaurant with no wait."}
            </p>
            <div className="mt-6 pt-5 border-t border-q5 space-y-1.5">
              {PHONES.map((p) => (
                <a
                  key={p.tel}
                  href={`tel:${p.tel}`}
                  data-testid={`pickup-call-${p.tel}`}
                  className="block text-white hover:text-gold transition-colors text-sm flex items-center gap-2"
                >
                  <Phone size={12} className="text-gold" />
                  {p.display}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Domicilio */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="card-delivery"
            className="bg-[#141414] border border-q5 hover:border-gold transition-colors p-7 flex flex-col"
          >
            <div className="w-12 h-12 border border-gold flex items-center justify-center mb-5">
              <Truck size={20} className="text-gold" />
            </div>
            <h3 className="font-serif-q5 text-2xl text-white mb-3">{t.call.delivery}</h3>
            <div className="space-y-2 text-sm">
              <p className="text-[#E5A050] flex items-start gap-2">
                <span className="text-gold mt-0.5">+</span>
                <span>{t.call.deliveryNote}</span>
              </p>
              <p className="text-neutral-300 flex items-start gap-2">
                <span className="text-gold mt-0.5">·</span>
                <span>{t.call.deliveryMinIn}</span>
              </p>
              <p className="text-neutral-300 flex items-start gap-2">
                <span className="text-gold mt-0.5">·</span>
                <span>{t.call.deliveryMinOut}</span>
              </p>
              <p className="text-[11px] text-neutral-500 italic pl-4">{t.call.deliveryExtraNote}</p>
            </div>
            <div className="flex-grow"></div>
            <div className="mt-6 pt-5 border-t border-q5 space-y-1.5">
              {PHONES.map((p) => (
                <a
                  key={p.tel}
                  href={`tel:${p.tel}`}
                  data-testid={`delivery-call-${p.tel}`}
                  className="block text-white hover:text-gold transition-colors text-sm flex items-center gap-2"
                >
                  <Phone size={12} className="text-gold" />
                  {p.display}
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        <p className="text-center text-xs text-neutral-500 uppercase tracking-[0.25em] flex items-center justify-center gap-2">
          <Info size={11} /> {t.call.hours}
        </p>
      </div>
    </section>
  );
};
